import { stem, Similar } from './search-text'
import { IVectors, readTable, cosine } from './word-vectors'

// Word vectors: how search finds a word it cannot place by its spelling —
// مامان for مادر, doctor for physician. Two tables, Persian and English, each a
// file under /vectors that scripts/build-vectors.ts makes from fastText's
// Common Crawl vectors. A word is only compared with words of its own
// language: the two tables were trained apart, and their numbers do not line
// up with each other.
//
// Fetched the first time search is opened. Until they arrive, and if they
// never do, search is the rules alone. See "Word vectors" in docs/search.md.

type Language = 'fa' | 'en'

const tables: Partial<Record<Language, IVectors>> = {}
let loading: Promise<void> | null = null

const languageOf = (term: string): Language | null =>
    /^[a-z]+$/.test(term) ? 'en' : /^[ؠ-يٱ-ە]+$/.test(term) ? 'fa' : null

interface IFound {
    table: IVectors
    at: number
}

// A word's place in its language's table: as written, or else by its stem.
function find(term: string): IFound | null {
    const language = languageOf(term)
    const table = language ? tables[language] : undefined
    if (!table) return null
    const at = table.index.get(term) ?? table.index.get(stem(term))
    return at === undefined ? null : { table, at }
}

// A word is near in meaning from a cosine of NEAR, which two words picked at
// random reach about one time in 800 in Persian and one in 400 in English
// (measured on the built tables). Every near word then
// counts the same, as much as a typo does, so meaning never outranks spelling.
// Flat rather than graded, because the nearest words by cosine are as often a
// relative or an opposite as a synonym: mom and dad, buy and sell. The cosine
// says yes or no, and the day decides the order.
const NEAR = 0.55
const STRENGTH = 0.6
export const strength = (cos: number) => cos >= NEAR ? STRENGTH : 0

// Both tables, fetched once. A failed fetch — offline on the web, before the
// service worker holds them — is forgotten, so the next visit tries again.
function load(): Promise<void> {
    if (!loading) {
        loading = Promise.all((['fa', 'en'] as Language[]).map(async language => {
            // fetch rather than baseline's http, which reads JSON or text:
            // these are bytes.
            const res = await fetch(`/vectors/${language}.bin`)
            if (!res.ok) throw new Error(`vectors ${language}: HTTP ${res.status}`)
            tables[language] = readTable(new Uint8Array(await res.arrayBuffer()))
        })).then(() => undefined, err => {
            console.warn('search: word vectors unavailable', err)
            loading = null
        })
    }
    return loading
}

// The meaning match rank() takes, for one query's words. Nothing until a
// table is in.
function similar(terms: string[]): Similar | undefined {
    if (!tables.fa && !tables.en) return undefined
    const found = terms.map(find)
    const known = terms.map(() => new Map<string, number>())
    return (q, term) => {
        const a = found[q]
        if (!a) return 0
        const hit = known[q].get(term)
        if (hit !== undefined) return hit
        const b = find(term)
        const s = b && b.table === a.table ? strength(cosine(a.table, a.at, b.at)) : 0
        known[q].set(term, s)
        return s
    }
}

export default { load, similar }
